import React, { useState, useRef, useEffect } from "react";
import Header from "../components/Header";
import { stockService } from "../services/apiService";
import Toast from "../components/Toast";
import JsBarcode from "jsbarcode";

const PAGE_SIZE = 20;

const LABEL_SIZES = [
  { name: "38x25mm", width: "38mm", height: "25mm", gap: 3, barcodeHeight: 15, fontSize: 8 },
  { name: "50x25mm", width: "50mm", height: "25mm", gap: 3, barcodeHeight: 18, fontSize: 9 },
  { name: "50x30mm", width: "50mm", height: "30mm", gap: 3, barcodeHeight: 22, fontSize: 9 },
  { name: "50x38mm", width: "50mm", height: "38mm", gap: 3, barcodeHeight: 22, fontSize: 9 },
  { name: "60x40mm", width: "60mm", height: "40mm", gap: 3, barcodeHeight: 28, fontSize: 10 },
];

const LabelPreview = ({ productName, barcode, mrp }) => {
  const canvasRef = useRef(null);

  React.useEffect(() => {
    if (canvasRef.current && barcode) {
      try {
        JsBarcode(canvasRef.current, barcode, {
          format: "CODE128", width: 1.2, height: 30, displayValue: true, fontSize: 8, margin: 1,
        });
      } catch { /* invalid */ }
    }
  }, [barcode]);

  return (
    <div className="border border-gray-300 rounded p-1.5 w-[180px] h-[90px] flex flex-col items-center justify-between bg-white">
      <span className="text-[9px] font-bold text-center truncate w-full">{productName}</span>
      <canvas ref={canvasRef} className="max-w-full" />
      <span className="text-[9px] font-bold">MRP: {mrp}</span>
    </div>
  );
};

const PrintLabels = () => {
  const [allStocks, setAllStocks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [labelQty, setLabelQty] = useState({});
  const [page, setPage] = useState(0);
  const [labelSize, setLabelSize] = useState(LABEL_SIZES[3]); // default 50x38mm
  const [printColumns, setPrintColumns] = useState(2);
  const printRef = useRef(null);

  useEffect(() => { loadStocks(); }, []);

  const loadStocks = async () => {
    setLoading(true);
    try {
      const data = await stockService.searchStock("", "", "", "", "", 0, 500);
      const list = Array.isArray(data) ? data : Array.isArray(data.content) ? data.content : [];
      setAllStocks(list.filter((s) => s.barcode || s.jasbillBarcode));
    } catch {
      setToast({ type: "error", message: "Failed to load stocks" });
      setAllStocks([]);
    } finally { setLoading(false); }
  };

  const filtered = allStocks.filter((s) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (s.productName || "").toLowerCase().includes(q) ||
      (s.batchNo || "").toLowerCase().includes(q) ||
      (s.manufacturer || "").toLowerCase().includes(q) ||
      (s.barcode || "").toLowerCase().includes(q) ||
      (s.jasbillBarcode || "").toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when search changes
  useEffect(() => { setPage(0); }, [searchTerm]);

  const toggleSelect = (stock) => {
    setSelected((prev) => {
      const exists = prev.find((s) => s.stockId === stock.stockId);
      if (exists) return prev.filter((s) => s.stockId !== stock.stockId);
      return [...prev, stock];
    });
    if (!labelQty[stock.stockId]) {
      setLabelQty((prev) => ({ ...prev, [stock.stockId]: 1 }));
    }
  };

  const selectAllPage = () => {
    const allPageSelected = paged.every((s) => selected.some((sel) => sel.stockId === s.stockId));
    if (allPageSelected) {
      setSelected((prev) => prev.filter((s) => !paged.some((p) => p.stockId === s.stockId)));
    } else {
      const newSelections = paged.filter((s) => !selected.some((sel) => sel.stockId === s.stockId));
      setSelected((prev) => [...prev, ...newSelections]);
      const qty = {};
      newSelections.forEach((r) => { qty[r.stockId] = labelQty[r.stockId] || 1; });
      setLabelQty((prev) => ({ ...prev, ...qty }));
    }
  };

  const totalLabelCount = selected.reduce((sum, s) => sum + (labelQty[s.stockId] || 1), 0);

  const clearSelection = () => {
    setSelected([]);
    setLabelQty({});
  };

  const handlePrint = () => {
    if (selected.length === 0) {
      setToast({ type: "error", message: "Select at least one stock to print labels" });
      return;
    }

    // Validate all selected have valid barcode
    const invalid = selected.filter((s) => !(s.jasbillBarcode || s.barcode));
    if (invalid.length > 0) {
      setToast({ type: "error", message: `${invalid.length} item(s) have no barcode value` });
      return;
    }

    const size = labelSize;
    const totalWidthMm = parseFloat(size.width) * printColumns;
    const totalWidth = `${totalWidthMm}mm`;

    // Flatten all labels into a single array
    const allLabels = [];
    selected.forEach((stock) => {
      const qty = labelQty[stock.stockId] || 1;
      for (let i = 0; i < qty; i++) {
        allLabels.push({ stock, idx: allLabels.length });
      }
    });

    // Build rows of `printColumns` labels each
    const rows = [];
    for (let i = 0; i < allLabels.length; i += printColumns) {
      rows.push(allLabels.slice(i, i + printColumns));
    }

    // Pre-render barcodes as SVG strings in main window
    const svgMap = {};
    allLabels.forEach(({ stock, idx }) => {
      const barcodeValue = stock.jasbillBarcode || stock.barcode;
      if (!barcodeValue) return;
      try {
        const svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        JsBarcode(svgEl, barcodeValue, {
          format: "CODE128", width: 1, height: size.barcodeHeight,
          displayValue: true, fontSize: size.fontSize - 1, margin: 0, textMargin: 0,
        });
        // Remove fixed width/height so CSS can control size
        svgEl.removeAttribute("width");
        svgEl.removeAttribute("height");
        svgMap[idx] = svgEl.outerHTML;
      } catch { svgMap[idx] = ""; }
    });

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      setToast({ type: "error", message: "Popup blocked! Please allow popups for this site and try again." });
      return;
    }
    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>Print Labels</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; }
  table { width: ${totalWidth}; border-collapse: collapse; table-layout: fixed; }
  td{
    width:${size.width};
    padding:0;
    vertical-align:top;
}
  .label {
    width: ${size.width};
    height: ${size.height};
    border: none;
    padding: 1mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;
    box-sizing: border-box;
  }
  .label .name {
    font-size: ${size.fontSize}px;
    font-weight: bold;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 100%;
    line-height: 1.2;
  }
  .label .mrp {
    font-size: ${size.fontSize}px;
    font-weight: bold;
    line-height: 1.2;
  }
  .label svg{
    width:100%;
    height:auto;
    display:block;
}
  @media print {
    @page { margin: 0; }
    html, body { margin: 0; padding: 0; }
    tr { page-break-inside: avoid; break-inside: avoid; }
  }
  @media screen {
    body { padding: 10px; background: #f5f5f5; }
    .label { background: #fff; border: 0.5mm solid #ccc; }
  }
</style>
</head><body><table>
`);

    rows.forEach((row) => {
      printWindow.document.write(`<tr>`);
      row.forEach(({ stock, idx }) => {
        printWindow.document.write(`
          <td><div class="label">
            <div class="name">${(stock.productName || "").replace(/</g, "&lt;")}</div>
            ${svgMap[idx] || ""}
            <div class="mrp">MRP: ${stock.mrp || "0"} | ${(stock.batchNo || "-").replace(/</g, "&lt;")}</div>
          </div></td>
        `);
      });
      // Fill empty cells if last row is incomplete
      for (let e = row.length; e < printColumns; e++) {
        printWindow.document.write(`<td><div class="label" style="border:none"></div></td>`);
      }
      printWindow.document.write(`</tr>`);
    });

    printWindow.document.write(`</table></body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.focus(); printWindow.print(); setTimeout(() => { printWindow.close(); }, 3000); }, 400);
  };

  const allPageSelected = paged.length > 0 && paged.every((s) => selected.some((sel) => sel.stockId === s.stockId));

  return (
    <div className="h-full flex flex-col bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4 overflow-hidden">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* Main Section */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">
            Print Barcode Labels
            {filtered.length > 0 && <span className="text-xs font-normal text-blue-600 ml-2">({filtered.length})</span>}
            {selected.length > 0 && <span className="text-xs font-normal text-green-600 ml-1">| {selected.length} selected</span>}
          </h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-44 px-3 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
            />
            <select
              value={labelSize.name}
              onChange={(e) => setLabelSize(LABEL_SIZES.find((s) => s.name === e.target.value))}
              className="px-2 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
            >
              {LABEL_SIZES.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <select
              value={printColumns}
              onChange={(e) => setPrintColumns(Number(e.target.value))}
              className="px-2 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
              title="Columns per row"
            >
              <option value={1}>1 Col</option>
              <option value={2}>2 Cols</option>
              <option value={3}>3 Cols</option>
              <option value={4}>4 Cols</option>
            </select>
            {selected.length > 0 && (
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 rounded-full font-semibold text-xs border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                <i className="fas fa-times mr-1" />Clear
              </button>
            )}
            <button
              onClick={handlePrint}
              disabled={selected.length === 0}
              className="px-4 py-1.5 rounded-full text-white font-semibold text-xs bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 shadow disabled:opacity-50"
              title={selected.length > 0 ? `Print ${totalLabelCount} label(s)` : ""}
            >
              <i className="fas fa-print mr-1" />Print {totalLabelCount > 0 ? `(${totalLabelCount})` : ""}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1">
          <table className="min-w-full text-sm">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2 px-3 text-center">
                  <input type="checkbox" checked={allPageSelected} onChange={selectAllPage} className="w-4 h-4" />
                </th>
                <th className="py-2 px-3 text-left">Product</th>
                <th className="py-2 px-3 text-left">Batch</th>
                <th className="py-2 px-3 text-left">Barcode</th>
                <th className="py-2 px-3 text-right">MRP</th>
                <th className="py-2 px-3 text-center">Labels</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="py-10 text-center text-blue-400">
                  <i className="fas fa-spinner fa-spin text-2xl" />
                </td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan="6" className="py-10 text-center text-gray-400">
                  <i className="fas fa-barcode text-4xl mb-3 block" />
                  <p className="text-sm">{searchTerm ? "No stocks match filter." : "No stocks with barcode found."}</p>
                </td></tr>
              ) : (
                paged.map((stock, idx) => {
                  const isSelected = selected.some((s) => s.stockId === stock.stockId);
                  const barcodeVal = stock.jasbillBarcode || stock.barcode;
                  return (
                    <tr key={stock.stockId} className={`border-t hover:bg-indigo-50 ${isSelected ? "bg-blue-50" : idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                      <td className="py-2 px-3 text-center">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(stock)} className="w-4 h-4" />
                      </td>
                      <td className="py-2 px-3 font-medium">{stock.productName}</td>
                      <td className="py-2 px-3 text-gray-600 text-xs">{stock.batchNo || "-"}</td>
                      <td className="py-2 px-3 text-xs text-gray-500 truncate max-w-[200px]" title={barcodeVal}>{barcodeVal || "-"}</td>
                      <td className="py-2 px-3 text-right">{stock.mrp || "0"}</td>
                      <td className="py-2 px-3 text-center">
                        <input
                          type="number" min="1" max="500"
                          value={labelQty[stock.stockId] || 1}
                          onChange={(e) => setLabelQty((prev) => ({ ...prev, [stock.stockId]: Math.max(1, parseInt(e.target.value) || 1) }))}
                          className="w-14 px-2 py-1 border border-blue-200 rounded text-xs text-center focus:ring-1 focus:ring-indigo-400 focus:outline-none"
                          disabled={!isSelected}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
            <span className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(0)} disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => setPage((p) => p - 1)} disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} onClick={() => setPage(i)}
                  className={`px-2 py-1 text-xs rounded border ${i === page ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}>{i + 1}</button>
              )).slice(Math.max(0, page - 2), page + 3)}
              <button onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          </div>
        )}
      </section>

      {/* Label Preview */}
      {selected.length > 0 && (
        <section className="mt-2 bg-white rounded-xl shadow-md border border-blue-200 p-3 flex-shrink-0">
          <h3 className="text-sm font-bold text-blue-800 mb-2">Preview ({labelSize.name})</h3>
          <div ref={printRef} className="flex flex-wrap gap-2 overflow-x-auto">
            {selected.slice(0, 6).map((stock) => (
              <LabelPreview
                key={stock.stockId}
                productName={stock.productName}
                barcode={stock.jasbillBarcode || stock.barcode}
                mrp={stock.mrp || "0"}
              />
            ))}
            {selected.length > 6 && (
              <div className="flex items-center text-xs text-gray-500 px-4">+{selected.length - 6} more</div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default PrintLabels;
